import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { map } from 'lodash';
import { scaleLog } from '@visx/scale';
import { useWordcloud } from '@visx/wordcloud';

import { wordFrequency } from 'js/utils/text';

const COLORS = ['#28b3d1', '#02758d', '#004958'];
const WORD_FADE_MS = 400;
const WORD_MOTION =
  'transform 0.7s ease, font-size 0.7s ease, opacity 0.4s ease';
const CLOUD_RANDOM = () => 0.2222;
const CLOUD_ROTATE = () => 0;

function colorFor(text) {
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash + text.charCodeAt(i)) % COLORS.length;
  }
  return COLORS[hash];
}

function CloudWords({ words = [] }) {
  const [exiting, setExiting] = useState([]);
  const [visible, setVisible] = useState(() => new Set());
  const prevWordsRef = useRef([]);
  const timeoutsRef = useRef(new Map());

  const incomingTexts = new Set(words.map((word) => word.text));
  const newlyGone = prevWordsRef.current.filter(
    (word) =>
      !incomingTexts.has(word.text) &&
      !exiting.some((item) => item.text === word.text)
  );
  prevWordsRef.current = words;

  const fading = [
    ...exiting.filter((word) => !incomingTexts.has(word.text)),
    ...newlyGone,
  ];
  const displayed = [
    ...words.map((word) => ({ ...word, exiting: false })),
    ...fading.map((word) => ({ ...word, exiting: true })),
  ];

  useLayoutEffect(() => {
    incomingTexts.forEach((text) => {
      const timeout = timeoutsRef.current.get(text);
      if (timeout) {
        window.clearTimeout(timeout);
        timeoutsRef.current.delete(text);
      }
    });

    if (newlyGone.length === 0) {
      return undefined;
    }

    setExiting((current) => {
      const byText = new Map(
        current
          .filter((word) => !incomingTexts.has(word.text))
          .map((word) => [word.text, word])
      );
      newlyGone.forEach((word) => {
        if (!byText.has(word.text)) {
          byText.set(word.text, { ...word, exiting: true });
        }
      });
      return [...byText.values()];
    });

    newlyGone.forEach((word) => {
      const timeout = window.setTimeout(() => {
        timeoutsRef.current.delete(word.text);
        setExiting((current) =>
          current.filter((item) => item.text !== word.text)
        );
        setVisible((current) => {
          if (!current.has(word.text)) {
            return current;
          }
          const next = new Set(current);
          next.delete(word.text);
          return next;
        });
      }, WORD_FADE_MS);
      timeoutsRef.current.set(word.text, timeout);
    });

    return undefined;
    // newlyGone/incomingTexts are from this words render; prevWordsRef is
    // already updated, so recompute-from-ref would miss departing words.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [words]);

  useEffect(() => {
    setVisible((current) => {
      let changed = false;
      const next = new Set(current);
      words.forEach((word) => {
        if (!next.has(word.text)) {
          next.add(word.text);
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [words]);

  useEffect(
    () => () => {
      timeoutsRef.current.forEach((timeout) => window.clearTimeout(timeout));
    },
    []
  );

  return displayed.map((word) => (
    <text
      key={word.text}
      fill={colorFor(word.text)}
      textAnchor="middle"
      fontWeight={word.weight}
      fontFamily={word.font}
      className={word.exiting ? 'is-exiting' : undefined}
      style={{
        transform: `translate(${word.x}px, ${word.y}px)`,
        fontSize: word.size,
        opacity: word.exiting || !visible.has(word.text) ? 0 : 1,
        transition: WORD_MOTION,
      }}
      onTransitionEnd={(event) => {
        if (!word.exiting || event.propertyName !== 'opacity') {
          return;
        }
        const timeout = timeoutsRef.current.get(word.text);
        if (timeout) {
          window.clearTimeout(timeout);
          timeoutsRef.current.delete(word.text);
        }
        setExiting((current) =>
          current.filter((item) => item.text !== word.text)
        );
        setVisible((current) => {
          if (!current.has(word.text)) {
            return current;
          }
          const next = new Set(current);
          next.delete(word.text);
          return next;
        });
      }}
    >
      {word.text}
    </text>
  ));
}

function CommentWordCloud({ responses = [], width = 200, height = 120 }) {
  const words = useMemo(
    () =>
      map(
        wordFrequency(
          responses
            .filter((r) => !r?.is_na)
            .map(({ value }) => value ?? '')
            .join(' ')
        ),
        (value, text) => ({ text, value })
      ),
    [responses]
  );

  const layoutScale = Math.max(height, 120) / 120;

  const fontScale = useMemo(() => {
    if (words.length === 0) {
      return null;
    }

    const values = words.map((w) => w.value);
    const min = Math.min(...values);
    const max = Math.max(...values);

    return scaleLog({
      domain: [min === max ? min / 2 || 0.5 : min, max],
      range: [10 * layoutScale, 30 * layoutScale],
    });
  }, [words, layoutScale]);

  const fontSizeSetter = useCallback(
    (d) => (fontScale ? fontScale(d.value) : 12),
    [fontScale]
  );

  const cloudWords = useWordcloud({
    words,
    width,
    height,
    fontSize: fontSizeSetter,
    font: 'Roboto',
    fontWeight: 'bold',
    padding: 3 * layoutScale,
    spiral: 'rectangular',
    rotate: CLOUD_ROTATE,
    random: CLOUD_RANDOM,
  });

  if (width <= 0 || height <= 0) {
    return null;
  }

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="comment-word-cloud">
      <g transform={`translate(${width / 2}, ${height / 2})`}>
        <CloudWords words={cloudWords} />
      </g>
    </svg>
  );
}

export default CommentWordCloud;
